import React, { useState, useRef } from 'react';
import {
  Box,
  IconButton,
  Slider,
  Typography,
  Paper,
  Tooltip
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  VolumeUp as VolumeIcon,
  VolumeOff as VolumeOffIcon
} from '@mui/icons-material';

const AudioPlayer = ({ 
  audioUrl, 
  title = "Audio Recording",
  showTitle = true,
  height = 60,
  width = "100%",
  onPlay,
  onPause,
  onEnded
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const audioRef = useRef(null);

  const formatTime = (time) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handlePlayPause = () => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
      onPause?.();
    } else {
      audioRef.current.play();
      setIsPlaying(true);
      onPlay?.();
    }
  };

  const handleStop = () => {
    if (!audioRef.current) return;
    audioRef.current.pause();
    audioRef.current.currentTime = 0;
    setIsPlaying(false);
    setCurrentTime(0);
    onPause?.();
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current) return;
    setCurrentTime(audioRef.current.currentTime);
  };

  const handleLoadedMetadata = () => {
    if (!audioRef.current) return;
    setDuration(audioRef.current.duration);
    setIsLoading(false);
  };

  const handleSeek = (event, newValue) => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = newValue;
    setCurrentTime(newValue);
  };

  const handleVolumeChange = (event, newValue) => {
    if (!audioRef.current) return;
    const newVolume = newValue / 100;
    setVolume(newVolume);
    audioRef.current.volume = newVolume;
    setIsMuted(newVolume === 0);
  };

  const handleMuteToggle = () => {
    if (!audioRef.current) return;
    if (isMuted) {
      audioRef.current.volume = volume;
      setIsMuted(false);
    } else {
      audioRef.current.volume = 0;
      setIsMuted(true);
    }
  };

  const handleLoadStart = () => {
    setIsLoading(true);
    setError(null);
  };

  const handleError = (e) => {
    console.error('Audio playback error:', e);
    setError('Failed to load audio file');
    setIsLoading(false);
    setIsPlaying(false);
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
    onEnded?.();
  };

  return (
    <Paper 
      elevation={1} 
      sx={{ 
        p: 2, 
        height, 
        width,
        display: 'flex',
        flexDirection: 'column',
        gap: 1
      }}
    >
      {showTitle && (
        <Typography variant="body2" color="text.secondary" noWrap>
          {title}
        </Typography>
      )}
      
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {/* Play/Pause Button */}
        <Tooltip title={isPlaying ? 'Pause' : 'Play'}>
          <IconButton 
            onClick={handlePlayPause}
            disabled={isLoading || !!error}
            color="primary"
            size="small"
          >
            {isPlaying ? <PauseIcon /> : <PlayIcon />}
          </IconButton>
        </Tooltip>

        {/* Stop Button */}
        <Tooltip title="Stop">
          <IconButton 
            onClick={handleStop}
            disabled={isLoading || !!error}
            size="small"
          >
            <StopIcon />
          </IconButton>
        </Tooltip>

        {/* Time Display */}
        <Typography variant="caption" sx={{ minWidth: 80 }}>
          {formatTime(currentTime)} / {formatTime(duration)}
        </Typography>

        {/* Progress Slider */}
        <Slider
          value={currentTime}
          max={duration || 100}
          onChange={handleSeek}
          disabled={isLoading || !!error}
          sx={{ flex: 1, mx: 2 }}
          size="small"
        />

        {/* Volume Control */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 120 }}>
          <Tooltip title={isMuted ? 'Unmute' : 'Mute'}>
            <IconButton onClick={handleMuteToggle} size="small">
              {isMuted ? <VolumeOffIcon /> : <VolumeIcon />}
            </IconButton>
          </Tooltip>
          <Slider
            value={isMuted ? 0 : volume * 100}
            onChange={handleVolumeChange}
            sx={{ width: 80 }}
            size="small"
          />
        </Box>
      </Box>

      {/* Error Display */}
      {error && (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      )}

      {/* Hidden Audio Element */}
      <audio
        ref={audioRef}
        src={audioUrl}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onLoadStart={handleLoadStart}
        onError={handleError}
        onEnded={handleEnded}
        preload="metadata"
      />
    </Paper>
  );
};

export default AudioPlayer; 